import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { H2, HeroH1, P, SeoPageShell, ctaClass } from "./seo/SeoPageLayout";

const PATH = "/chatgpt-vs-greyrock-ai";

const ROWS: [string, string, string][] = [
  ["Built for", "General-purpose assistant", "Detecting manipulation patterns in messages"],
  ["Pattern framework", "None fixed; depends on how you ask", "18-pattern taxonomy (gaslighting, DARVO, guilt-tripping, invalidation, and more)"],
  ["Shows the evidence", "Sometimes, if you ask", "Full reports quote the exact words behind each pattern"],
  ["Severity score", "No consistent scale", "Full reports score severity on a fixed ladder"],
  ["Consistency", "Answers can vary between runs and phrasings", "Same message, same pattern framework, same scale"],
  ["Prompting needed", "Yes; results depend on your question", "No; paste the message and analyze"],
  ["Practice responding", "Possible with careful prompting", "Free 3-turn practice after every matched result, plus the full simulator when you sign in"],
  ["Cost to try", "Free tier available", "3 free pattern previews, no signup; full reports when you sign in"],
];

const FAQ = [
  {
    q: "Can ChatGPT detect gaslighting?",
    a: "ChatGPT can discuss gaslighting and may recognize it in a message, but it has no fixed framework for doing so, and its answers can change based on how you phrase your question. GreyRock AI checks messages against the same 18-pattern taxonomy, and its full reports quote the words behind each pattern.",
  },
  {
    q: "Is GreyRock AI just ChatGPT with a different interface?",
    a: "No. GreyRock uses an AI model alongside a deterministic rule layer, a fixed manipulation taxonomy, and a consistent severity scale. The structure is what makes results comparable from one message to the next.",
  },
  {
    q: "Is GreyRock AI free?",
    a: "Your first 3 pattern previews are free, with no account required. Full reports, with quoted evidence and a severity score, are available when you sign in.",
  },
  {
    q: "Does GreyRock store my messages?",
    a: "Free previews run entirely in your browser, and your message is never sent to our servers. If you sign in and run a full analysis, the report is saved to your account's private Reports library so you can come back to it.",
  },
  {
    q: "Can I use both?",
    a: "Yes. Many people use GreyRock to name what happened, then talk it through elsewhere.",
  },
];

const Li = ({ children }: { children: React.ReactNode }) => (
  <li className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">{children}</li>
);

const ChatgptVsGreyrockAi = () => {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <SeoPageShell>
      <SEO
        title="ChatGPT vs GreyRock AI: Which Is Better for Spotting Manipulation in Texts?"
        description="ChatGPT can talk you through a hard conversation. GreyRock AI is built to do one thing: name the manipulation patterns in a text, quote the exact words, and score severity the same way every time."
        path={PATH}
        jsonLd={faqJsonLd}
      />

      <HeroH1>ChatGPT vs GreyRock AI</HeroH1>
      <p className="text-[18px] md:text-[19px] leading-[1.7] text-white/80">
        A lot of people paste a confusing text into ChatGPT and ask, "Is this gaslighting?" It's a
        reasonable instinct. ChatGPT is a capable general assistant, and it can help you think. But
        it wasn't built to analyze manipulation, and that shows in a few specific ways. Here's an
        honest comparison so you can pick the right tool for what you need.
      </p>

      <H2>The short answer</H2>
      <P>
        Use ChatGPT when you want to talk something through, brainstorm, or get general advice. Use
        GreyRock AI when you want to know exactly what a message is doing: which manipulation
        pattern it uses, which words show it, and how serious it is.
      </P>

      <H2>Side-by-side comparison</H2>
      <div className="overflow-x-auto -mx-5 px-5">
        <table className="w-full min-w-[560px] border-collapse text-left text-[15px] md:text-[16px] leading-relaxed">
          <thead>
            <tr className="border-b border-cyan-400/20">
              <th className="py-3 pr-4 font-mono uppercase tracking-[0.15em] text-[11px] text-white/45">
                Feature
              </th>
              <th className="py-3 pr-4 font-mono uppercase tracking-[0.15em] text-[11px] text-white/45">
                ChatGPT
              </th>
              <th className="py-3 font-mono uppercase tracking-[0.15em] text-[11px] text-cyan-300/80">
                GreyRock AI
              </th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map(([feature, chatgpt, greyrock]) => (
              <tr key={feature} className="border-b border-white/[0.06] align-top">
                <td className="py-3 pr-4 font-semibold text-white/85">{feature}</td>
                <td className="py-3 pr-4 text-white/60">{chatgpt}</td>
                <td className="py-3 text-white/80">{greyrock}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H2>Why the question you ask matters</H2>
      <P>
        When you ask ChatGPT "Is my partner gaslighting me?", you've already framed the answer.
        General-purpose AI tends to work with the framing it's given, so it may echo your worry
        back to you, or soften it, depending on how you word things. That's useful for
        conversation, but not for clarity.
      </P>
      <P>
        GreyRock works differently. It reads the message itself, not your summary of it, and checks
        it against a fixed set of manipulation patterns. You don't have to know what to ask.
      </P>

      <H2>Why consistency matters</H2>
      <P>
        If you analyze the same message twice and get two different verdicts, it's hard to trust
        either one, especially when you're already second-guessing yourself. GreyRock uses an AI
        model too, but wraps it in a fixed pattern taxonomy and a deterministic severity scale, so
        a guilt trip is scored as a guilt trip every time.
      </P>

      <H2>When ChatGPT is the better choice</H2>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <Li>You want to talk through how you feel, not analyze a specific message</Li>
        <Li>You need broad advice on relationships, work, or family situations</Li>
        <Li>You want help writing something long, like an email or a letter</Li>
      </ul>
      <P>GreyRock doesn't try to replace a conversation partner. It does one job.</P>

      <H2>When GreyRock AI is the better choice</H2>
      <ul className="list-disc pl-6 space-y-2 mt-4">
        <Li>A message left you confused and you can't explain why</Li>
        <Li>You want to know the name for what happened</Li>
        <Li>You want to see the exact words that crossed a line</Li>
        <Li>You want to rehearse your reply before you send it</Li>
      </ul>

      <H2>What neither tool replaces</H2>
      <P>
        Neither ChatGPT nor GreyRock is a therapist, a lawyer, or a diagnosis. GreyRock names
        patterns in language; it doesn't judge a person or prove intent. If you're in danger,
        contact local emergency services or a domestic violence hotline.
      </P>

      {/* CTA block */}
      <section className="mt-16 rounded-xl border border-cyan-400/20 bg-cyan-400/[0.04] px-6 py-10 text-center">
        <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-4">
          See what the message is actually doing
        </h2>
        <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75 max-w-[520px] mx-auto">
          Paste a text and see which manipulation pattern it resembles. 3 free previews, no signup.
        </p>
        <div className="mt-8">
          <Link to="/" className={ctaClass}>
            Analyze a message →
          </Link>
        </div>
      </section>

      <H2>FAQ</H2>
      {FAQ.map((f) => (
        <div key={f.q} className="mt-8 first:mt-0">
          <h3 className="text-[17px] md:text-[19px] font-semibold tracking-tight text-white mb-3 leading-snug">
            {f.q}
          </h3>
          <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">{f.a}</p>
        </div>
      ))}

      {/* Related reading */}
      <div className="mt-14 pt-6 border-t border-white/[0.06]">
        <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/40 mb-4 text-center">
          Related reading
        </p>
        <ul className="space-y-3 text-center">
          <li>
            <Link
              to="/free-gaslighting-detector"
              className="text-[14px] md:text-[15px] text-cyan-300/85 hover:text-cyan-200 transition-colors underline-offset-4 hover:underline"
            >
              Try the free gaslighting detector →
            </Link>
          </li>
          <li>
            <Link
              to="/analysis/darvo-in-text-messages"
              className="text-[14px] md:text-[15px] text-cyan-300/85 hover:text-cyan-200 transition-colors underline-offset-4 hover:underline"
            >
              DARVO in text messages →
            </Link>
          </li>
          <li>
            <Link
              to="/manipulative-text-message-analysis"
              className="text-[14px] md:text-[15px] text-cyan-300/85 hover:text-cyan-200 transition-colors underline-offset-4 hover:underline"
            >
              Manipulative text message analysis →
            </Link>
          </li>
          <li>
            <Link
              to="/ai-detect-narcissistic-abuse"
              className="text-[14px] md:text-[15px] text-cyan-300/85 hover:text-cyan-200 transition-colors underline-offset-4 hover:underline"
            >
              Can AI detect narcissistic abuse? →
            </Link>
          </li>
        </ul>
      </div>
    </SeoPageShell>
  );
};

export default ChatgptVsGreyrockAi;
